import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { CurrentUser } from '../../../common/providers/current-user';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { TeamsFormComponent } from '../../components/form/form';
import { AthletesProvider } from '../../../athletes/providers/athletes-provider';
import { CoachesProvider, CoachRow } from '../../../coaches/providers/coaches-provider';
import { TeamRow, TeamsProvider } from '../../providers/teams-provider';
import { SimpleMatDataSource } from '../../../common/providers/simple-mat-data-source';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TeamsCoachesFormComponent } from '../../components/form/coaches';
import { TeamsAthletesFormComponent } from '../../components/form/athletes';
import { TeamRights } from '../../providers/teams-rights';
import { forkJoin, Observable, of, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatTableModule} from "@angular/material/table";
import { NgIf } from "@angular/common";
import { MatIconButton } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { Team } from "../../../common/models/team";
import { catchError, map } from "rxjs/operators";
import { Paginator } from "../../../common/models/paginator";
import { ApiPaginator } from "../../../common/components/api-paginator/api-paginator";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";
import { Coach } from "../../../common/models/coach";
import { OrganizationRole } from "../../../common/models/organization-role";
import { Athlete } from "../../../common/models/athlete";

@Component({
  selector: 'teams-list-page',
  providers: [],
  styleUrls: ['./list.scss'],
  standalone: true,
  imports: [
    HeaderComponent,
    Error404Component,
    ModuleContentComponent,
    Error401Component,
    PageContentComponent,
    MatFormFieldModule,
    MatTableModule,
    MatSortModule,
    TranslateModule,
    NgIf,
    MatInputModule,
    MatIconModule,
    MatIconButton,
    MatMenuModule,
    ApiPaginator,
    CenterLoaderComponent,
  ],
  templateUrl: './list.html'
})
export class TeamsListPageComponent implements AfterViewInit, OnDestroy {
    public DESCRIPTION_MAX_LENGTH = 5000;

    public pageTitle = 'Teams_List';
    public mainActions: ModuleMenu[] = [];
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public activePage: number = 1;
    public activeSortDirection: string = 'asc';
    private teamFilter = '';

    public organization: Organization = {} as Organization;
    public availableCoaches: User[] = [];
    public availableAthletes: User[] = [];
    public teamsPaginator: Paginator = {} as Paginator;

    public rights: TeamRights = new TeamRights(this._utils);
    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['team', 'coach', 'actions'];

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort | undefined;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private _title: Title,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _router: Router,
        private _dialoger: MatDialog,
        private _toaster: MatSnackBar,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _dialog: MatDialog,
        private _athletesProvider: AthletesProvider,
        private _coachesProvider: CoachesProvider,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _teamsProvider: TeamsProvider,
        private cdr: ChangeDetectorRef,
    ) {
    }

    public ngAfterViewInit() {
      this._route.params.pipe(
        takeUntil(this.subscriptions),
        switchMap(() => this.loadOrganization()), // Assuming loadOrganization returns an Observable
        catchError((error) => {
          this.onError(error);
          return of(null); // Handle the error and continue the stream with a null value
        })
      ).subscribe({
        next: () => {
          this.initPage();
        },
        error: (err) => this.onError(err) // Additional error handling if needed
      });
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    // PUBLIC METHODS

    public onAddTeamClicked(): void {
        const dialogRef: any = this._dialog.open(TeamsFormComponent, {
            data: {
                organization: this.organization,
                availableCoaches: this.availableCoaches,
                availableAthletes: this.availableAthletes,
                formTitle: 'Team_creation',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: TeamRow) => {
                if (row) {
                  this.setTeamsRows({}).subscribe();
                }
            })
        );
    }

    public onModifyTeamClicked(toModify: TeamRow): void {
        const dialogRef: any = this._dialog.open(TeamsFormComponent, {
            data: {
                organization: this.organization,
                availableCoaches: this.availableCoaches,
                availableAthletes: this.availableAthletes,
                teamRow: toModify,
                formTitle: 'Team_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: TeamRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).team.id === row.team.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onManageCoachsClicked(toModify: TeamRow): void {

        const dialogRef: any = this._dialog.open(TeamsCoachesFormComponent, {
            data: {
                organization: this.organization,
                availableCoaches: this.availableCoaches,
                teamRow: toModify,
                formTitle: 'Team_Coach_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: TeamRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).team.id === row.team.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onManageAthletesClicked(toModify: TeamRow): void {

        const dialogRef: any = this._dialog.open(TeamsAthletesFormComponent, {
            data: {
                organization: this.organization,
                availableAthletes: this.availableAthletes,
                teamRow: toModify,
                formTitle: 'Team_Athlete_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: TeamRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).team.id === row.team.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onDeleteTeamClicked(toDelete: TeamRow): void {

        const dialog: MatDialogRef<any> = this._dialoger.open(ConfirmDialogComponent, {
            data: {
                confirmText: this._translater.instant('Confirm_delete_team')
            }
        });
        dialog.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.isLoading = true;  // Show a loading indicator
            const deleteSub = this.deleteTeam(toDelete.team).subscribe({
              next: () => {
                this.isLoading = false;  // Hide loading indicator on success
                this._toaster.open(this._translater.instant('Team_deleted'), '', {duration: 5000});
              },
              error: (error) => {
                this.isLoading = false;  // Ensure loading indicator is also hidden on error
                this.onError(error);  // Handle the error properly
              }
            });
            this.subscriptions.push(deleteSub);  // Add the subscription to the subscriptions to manage it properly
          }
        });
    }

    public onDisplayTeamClicked(toView: TeamRow): void {

        const url = '/' + this.organization!.code + '/teams/' + toView.team.id;
        this._router.navigateByUrl(url);
    }

    public onSortChange(event: any): void {

      if (event.active === 'name') {
        this.setTeamsRows({sort: event.direction}).subscribe();
      }
      this.dataSource.sortBy(event.active, event.direction);
    }

    public onFilterKeyup(filter: string): void {
      // a new search always restarts on the first page (#476)
      this.setTeamsRows({teamName: filter, page: 1}).subscribe({
        error: (error) => this.onError(error),
      });
    }


    private loadOrganization(): Observable<void> {
      return this._organizationNavigation.getOrganisationFromRoute().pipe(
        map((organization: Organization|null) => {
          if (organization) {
            this.organization = organization;
          } else {
            throw {error: 'Organization not found', status: 404};
          }
        })
      );
    }

  private initPage(): void {
    this.loadData().subscribe({
      next: () => {
        this.initNav();
        this.initSorters();
        this.initSearch();
        this.dataSource.sortBy('name', 'asc');
        this.isLoading = false;
      },
      error: (error) => this.onError(error)
    });
  }

    private initNav(): void {

        if (!this.rights.LIST) {
            this.is401 = true;
            return;
        }

        this.dataSource.paginator = this.paginator!;

        if (this.rights.ADD) {
            this.mainActions = [
                {title: 'Add', icon: 'add', url: null, onClick: () => this.onAddTeamClicked()}
            ];
        }

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private initSorters(): void {
        this.dataSource.addSortDefinition(
            'team',
            (a: TeamRow, b: TeamRow) => this._utils.sortRow(a.team.name.toLowerCase(), b.team.name.toLowerCase()),
            (a: TeamRow, b: TeamRow) => this._utils.sortRow(b.team.name.toLowerCase(), a.team.name.toLowerCase())
        );
        this.dataSource.addSortDefinition(
            'coach',
            (a: TeamRow, b: TeamRow) => this._utils.sortRow(a.coachNames.toLowerCase(), b.coachNames.toLowerCase()),
            (a: TeamRow, b: TeamRow) => this._utils.sortRow(b.coachNames.toLowerCase(), a.coachNames.toLowerCase())
        );
    }

    private initSearch(): void {
        this.dataSource.sort = this.sorter!;
        this.dataSource.filterPredicate = (data: TeamRow, filter: string) => {
            const str: string = data.team.name.toLowerCase();
            return str.includes(filter);
        };
    }

    private loadData(): Observable<any> {
      if (!this.organization) {
        return throwError(() => new Error('Organization not found'));
      }
      return forkJoin([
        this.setTeamsRows({}),
        this._coachesProvider.getCoaches(this.organization.id!),
        this._athletesProvider.getAthletes(this.organization.id!),
      ]).pipe(
        map(([teamRows, coaches, athletes]: [void, User[], User[]]) => {
          this.availableCoaches = coaches;
          this.availableAthletes = athletes;

          this.rights.evaluateScreenRights({
              rolesIds: this._currentUserRoles.getRolesIds(this.organization.id!)
          });
        }),
      );
    }

    public pageChange(page: number): void {
      this.isLoading = true;
      this.setTeamsRows({page: page}).subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.onError(error);
        },
      });
    }

   private setTeamsRows({sort = this.activeSortDirection, page = this.activePage, teamName = this.teamFilter}: {sort?:string, page?:number, teamName?:string}) : Observable<void> {
      return this._teamsProvider.getTeamsPaginator({
        organizationId: this.organization.id!,
        sortOrder: sort,
        pageNumber: page,
        teamName: teamName,
      }).pipe(
        map((teamsPaginator: Paginator) => {
          // Build the rows before publishing any state: a row that fails to build must not
          // leave the paginator showing a count for rows the table never receives (#476).
          const teams: TeamRow[] = teamsPaginator.data.map((team: Team) =>
            this._teamsProvider.createTeamRow(this.organization?.id!, team));

          this.activePage = page;
          this.activeSortDirection = sort;
          this.teamFilter = teamName;
          teamsPaginator.data = teams;
          this.teamsPaginator = teamsPaginator;
          this.dataSource.replaceAllWith(teams);

          this.dataSource.paginator = this.paginator!;
          return;
        })
      );
   }

    private deleteTeam(team: Team): Observable<void> {
      return this._teamsProvider.deleteTeam(this.organization.id!, team).pipe(
        map(() => {
          for (let i = 0; i < this.dataSource.count; i++) {
              if (this.dataSource.get(i).team.id === team.id) {
                  this.dataSource.remove(i);
                  i = this.dataSource.count;
              }
          }
        })
      );
    }

    private createPageTitle(){
        this.pageTitle = this._translater.instant('Teams_list');
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        this.breadcrumbProvider.setDefaultCrumbs([{
            shortTitle: 'Teams',
            title: 'Teams',
            url: this._router.url
        }]);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        }
    }
}
